import { useDeferredValue, useMemo, useRef, useState } from 'react'
import { Icon } from './icons'
import type { VoiceGroup, VoiceInfo } from '@/api/types'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { useVoicePreview, type VoicePreviewState } from '@/hooks/useVoicePreview'

/**
 * 音色库 —— 一个能**逛**的音色清单（2026-10-08，参照 MiniMax 语音库的信息结构做的）。
 *
 * ## 为什么不是下拉
 *
 * 下拉能「选」，但是不能「逛」。默认引擎下有 **303 条**音色、9 个分组，
 * 绝大多数是用户从名字上看不出差别的（「清透女声」和「从容女声」差在哪？）。
 * 只能靠名字猜的下场是：反复试听、反复重新生成，或者干脆一直用默认那一条。
 * 所以这里要的是**一屏能比较**：分组并排、关键词可搜、每条都能当场听。
 *
 * ## 结构（与参考站对齐，但删掉了我们做不到的东西）
 *
 * ```
 * ┌ 当前音色（+试听） ┐ ┌ 音色库（+搜） ┐      ← 两张「状态卡」，点右边圆钮就是动作
 *                          已收藏 3 / 共 303 条  ← 右对齐统计
 * [ 音色库 ] [ 收藏音色 ]                      ← 页签：清单来源
 * [ 🔍 搜索音色名或上游 ID        ] [ ↻ 重置 ]   ← 关键词
 * 按分组： [全部 303] [中文 · 通用 8] [粤语 6] …  ← 分组筛选（真源 = 接口下发的分组）
 * 中文 · 通用  8
 *   ▶ 温婉女声        默认  [普通话]      [选择] [☆]
 *     Chinese (Mandarin)_Warm_Girl
 * ```
 *
 * ⚠️ 参考站那行叫「试试：」，后面跟的是它编的几个**搜索示例**
 * （「职场粤语女声」这种）。我们这行给的是**分组名 + 条数**，是筛选器不是示例，
 * 所以标签如实写成「按分组：」—— 照抄「试试」会让人以为点一下是去搜关键词。
 *
 * 🔴 **参考站有三个页签，这里只有两个**：它的「我的音色」放的是**用户克隆的音色**，
 * 我们**没有克隆能力**（上游那条链路没接）。摆一个点进去永远是空的页签，
 * 就是拿一个做不到的功能占位 —— 界面说的必须是能做的。
 * 「收藏」则真的能存（存本机浏览器），所以留下。
 *
 * ## 三条口径
 *
 * ① 🔴 **音色清单来自接口，一条都不硬编码**。分组名、条数、顺序全部照接口下发，
 *    后端加一组这里自然出现。硬编码一份的话，上游删掉一条、页面照样发出去 ——
 *    症状是**选了 A 出来 B**，而任务状态全绿（上游对不认识的音色 ID 是悄悄换默认音色）。
 *
 * ② **行上的语言标签是从上游音色 ID 的前缀派生的，且认不出就不显示**（fail-closed）。
 *    它不是第二份真源 —— 标签的来源就是**我们要发出去的那个字符串本身**，
 *    所以不可能与载荷不一致。为什么不用分组名代替：分组只到「其他语种」（169 条），
 *    那一条下面其实混着葡/西/俄/法/德…… 光看分组挑不出来。
 *    ⚠️ 不做「取第一个下划线前的词」这种通用解析：`Santa_Claus` 会解析出 `Santa`，
 *    于是「圣诞老人」被标成一种语言。白名单之外一律不显示。
 *
 * ③ **收藏存本机**（`hw_voiceFavs`），不进后端、不进载荷 —— 它是**浏览偏好**，
 *    不是这条链路的一部分。相应地，换了引擎之后收藏里可能有当前引擎不存在的 ID，
 *    这时如实说一句「N 条在当前引擎下不存在」，而不是假装收藏是空的。
 */

/* ---------------------------- 语言前缀 → 中文标签 ---------------------------- */

/**
 * 上游音色 ID 的语言前缀 → 中文标签。**认不出就不显示**（见头注 ②）。
 *
 * ⚠️ 这不是「清单类枚举」（那种必须由后端下发）：它不下发任何可选值、
 * 不参与提交、也不影响花多少钱，只是把上游 ID 里那个英文词翻成中文给人看。
 * 而且它是 **fail-closed** 的 —— 上游加了一种新语言，这里最多是「少一个标签」，
 * 绝不会标错。**不许**为了「看起来全」而改成「猜一个最像的」。
 */
const LANG_BY_PREFIX: Record<string, string> = {
  'Chinese (Mandarin)': '普通话',
  Cantonese: '粤语',
  English: '英语',
  Japanese: '日语',
  Korean: '韩语',
  Spanish: '西班牙语',
  Portuguese: '葡萄牙语',
  Russian: '俄语',
  French: '法语',
  German: '德语',
  Italian: '意大利语',
  Arabic: '阿拉伯语',
  Turkish: '土耳其语',
  Dutch: '荷兰语',
  Ukrainian: '乌克兰语',
  Indonesian: '印尼语',
  Vietnamese: '越南语',
}

/** 取上游 ID 的语言标签；认不出返回 null（调用方不渲染标签） */
function langOf(voiceId: string): string | null {
  const cut = voiceId.indexOf('_')
  if (cut <= 0) return null
  return LANG_BY_PREFIX[voiceId.slice(0, cut)] ?? null
}

/* --------------------------------- 头像 --------------------------------- */

/**
 * 十四张证件照风人像（`public/voice-avatars/`，256px webp，合计约 78KB）。
 *
 * ## 为什么是「十四张 + 顺序轮转」，不是「303 条各一张」
 *
 * 音色清单是**上游下发的**（303 条，还会变）。给每一条都备一张图，意味着
 * 「上游加一条音色，我们就要补一张图」—— 而这份清单本来就不该由前端维护
 * （见头注 ①）。固定十四张、按规则分配，上游怎么变都不会缺图。
 *
 * ## 🔴 它和 `LANG_BY_PREFIX` 是同一类东西
 *
 * **纯展示、fail-closed、不参与提交**。挑脸挑得再不准，最坏结果也只是
 * 「头像气质跟声音不搭」，绝不会影响被发出去的音色 ID —— 而那个 ID 才是
 * 这一页唯一不能错的值。所以这里**不是**「清单类枚举」，不受「清单必须由
 * 后端下发」那条约束（那条管的是**可选值**）。
 *
 * ## 为什么用真人照片而不是色块 + 图标
 *
 * 2026-10-09 用户原话：「头像应该是人啊」。色块能区分行与行，但回答不了
 * 「这一条是谁的声音」—— 而列表里 99% 的音色名（「温暖女声」和「柔和女声」
 * 差在哪）只能靠听。一张脸至少让「刚才听的是哪一条」在视觉上留得住。
 * 十四张图由 duomi-image-2（gpt-image-2.5-flare）生成，同一套 prompt 模板
 * （浅灰底 / 正面头肩 / 柔和均匀光），所以看起来是一个系列。
 */
const AVATAR_MALE = ['male-1', 'male-2', 'male-3', 'male-4', 'male-5', 'male-6'] as const
const AVATAR_FEMALE = ['female-1', 'female-2', 'female-3', 'female-4', 'female-5', 'female-6'] as const
const AVATAR_NEUTRAL = ['neutral-1', 'neutral-2'] as const
/** 认不出性别时用的池子：**全部十四张**，不是那两张中性脸（见 `buildAvatarMap`） */
const AVATAR_ALL = [...AVATAR_MALE, ...AVATAR_FEMALE, ...AVATAR_NEUTRAL] as const

/**
 * 判女声。
 *
 * 🔴 **必须先判它**：`female` 里含 `male`、`woman` 里含 `man` —— 顺序反过来，
 * 一百多条女声会被整片判成男声，而界面上「每行都有个头像」，不会报任何错。
 */
const FEMALE_HINT =
  /female|girl|woman|women|lady|hostess|miss\b|queen|wife|mother|mom\b|daughter|sister|aunt|antie|maiden|heroine|princess|madam|bride|shaonv|chengshu|tianmei|xiaoling|mengmei|yujie|xuemei|xuejie/i

/**
 * 判男声。
 *
 * ⚠️ `(?<!wo)man\b` 那两个 lookbehind 不是装饰：
 *  · `(?<!wo)` 挡 `woman`（本来靠「先判女」已经挡住，双保险）；
 *  · `\b` 挡 `DeterminedManager` / `SensibleManager` 这类 —— `manager` 里
 *    正好含 `man`，不加词边界会把十几条**不分性别**的经理/主管判成男声。
 */
const MALE_HINT =
  /(?<!wo)man\b|male|boy|guy|gentleman|bloke|butler|knight|king\b|husband|godfather|santa|father|dad\b|brother|uncle|nephew|didi\b|nanyou|xuedi\b|xiongzhang|shaoye|junlang/i

/**
 * 整份清单一次算完：音色 ID → 头像路径。
 *
 * ## 为什么不是「按 ID 哈希挑一张」（`hashOf` 那套）
 *
 * 哈希挑脸是稳定的，但**池子只有六张，撞脸是必然的**。第一版就是这么写的，
 * 实测韩语那 49 条里一屏能看到三、四行是同一个人 —— 看起来像列表坏了。
 * 改成**按性别分桶、桶内顺序轮转**之后：完整清单里相邻的两条同性别音色
 * **必然不同脸**，每张脸出现的次数也摊平了（哈希那版 `female-1` 一个人占 45 行）。
 *
 * ## ⚠️ 代价说清楚
 *
 * 序号来自**清单顺序**，所以上游调整顺序、我们切引擎（清单完全不同）时，
 * 同一条音色的脸会变。这可以接受 —— 用户记不住 300 条里某一条的脸，
 * 但一眼就能看出「一屏里反复出现同一个人」。
 * 反过来说：**不许改成按「当前显示的第几行」轮转** —— 那样搜索、筛选之后
 * 脸会跟着变，同一条音色在两次渲染里换人，那才是真的坏。
 *
 * ## 认不出性别的走全部十四张
 *
 * 那 169 条其他语种里有一大半名字根本没性别信息（`Narrator` / `PowerfulSoldier`
 * / `GrimReaper` / `Robot_Armor` / `cartoon_pig`）。只在那两张中性脸里轮转的话，
 * 二十多条会变成两张脸交替刷屏，比撞脸更难看。头像不承载功能语义，
 * **多样性优先于性别准确**。
 */
function buildAvatarMap(groups: VoiceGroup[]): Map<string, string> {
  const map = new Map<string, string>()
  let fi = 0
  let mi = 0
  let ni = 0
  for (const g of groups) {
    for (const v of g.voices) {
      const name = FEMALE_HINT.test(v.id)
        ? AVATAR_FEMALE[fi++ % AVATAR_FEMALE.length]
        : MALE_HINT.test(v.id)
          ? AVATAR_MALE[mi++ % AVATAR_MALE.length]
          : AVATAR_ALL[ni++ % AVATAR_ALL.length]
      map.set(v.id, `/voice-avatars/${name}.webp`)
    }
  }
  return map
}

/* --------------------------------- 单行 --------------------------------- */

function VoiceRow({
  voice,
  avatar,
  active,
  isDefault,
  fav,
  preview,
  onPick,
  onFav,
}: {
  voice: VoiceInfo
  /** 这一条的头像路径。**由 `buildAvatarMap` 统一算好传进来**，不在行内自己挑
   *  （理由见 `buildAvatarMap`：挑脸要依赖整份清单的顺序，行内拿不到） */
  avatar: string
  active: boolean
  /** 是不是当前引擎的默认音色（后端 `ENGINE_DEFAULTS` 下发）—— 打一个「默认」标 */
  isDefault: boolean
  fav: boolean
  preview: VoicePreviewState
  onPick: () => void
  onFav: () => void
}) {
  const isThis = preview.active === voice.id
  const playing = isThis && preview.playing
  const busy = isThis && preview.busy
  const heard = preview.heard(voice.id)
  const lang = langOf(voice.id)

  return (
    <li className="vlib__row" data-active={active}>
      {/*
        缩略图 = 头像 + 试听键（与参考站一致）。
        ⚠️ 它是一个真按钮，不是图上画一个三角 —— 试听会**真花钱**，
        所以 title 上要把费用口径写出来，用户点之前就能看见。

        头像**常驻**（不是 hover 才出）：「这一条是谁的声音」是这一屏的主要信息；
        播放/暂停键退成右下角一枚小徽标，播放中才常亮 —— 常驻一块遮罩会把脸挡住。
      */}
      <button
        type="button"
        className="vlib__thumb"
        data-heard={heard}
        data-playing={playing || busy}
        title={
          busy
            ? '正在合成这一条…再点一下可以放弃等待'
            : playing
              ? '停止试听'
              : heard
                ? '再听一遍。这条已经合成过，重播不再计费'
                : '试听。首次会真合成一次（按条计费，¥0.05），同一音色再听不重复计费'
        }
        aria-label={`试听音色 ${voice.name}`}
        data-voice-preview={voice.id}
        onClick={() => preview.toggle(voice.id)}
      >
        <img
          className="vlib__thumbImg"
          src={avatar}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
        />
        <span className="vlib__thumbKey" aria-hidden="true">
          {busy ? <span className="vlib__spin" /> : <Icon name={playing ? 'pause' : 'play'} size={9} />}
        </span>
      </button>

      <span className="vlib__meta">
        <span className="vlib__nameRow">
          <span className="vlib__name">{voice.name}</span>
          {isDefault ? <span className="vlib__tag vlib__tag--default">默认</span> : null}
          {lang ? <span className="vlib__tag">{lang}</span> : null}
        </span>
        {/*
          第二行放**上游音色 ID**（就是会被发出去的那个字符串）。
          不显示它的话，「我以为选的是这个」没有任何地方能对账 ——
          后端日志、用量明细、排查记录里出现的都是这个 ID。
        */}
        <span className="vlib__id" title={voice.id}>
          {voice.id}
        </span>
      </span>

      <button
        type="button"
        className="vlib__star"
        data-active={fav}
        aria-pressed={fav}
        title={fav ? '取消收藏' : '收藏。只存在本机浏览器，不上传'}
        onClick={onFav}
      >
        <Icon name="star" size={15} />
      </button>

      <button
        type="button"
        className="vlib__pick"
        data-active={active}
        title={active ? '当前使用的音色' : '用这个音色'}
        onClick={onPick}
      >
        {active ? <Icon name="check" size={13} /> : null}
        {active ? '已选' : '选择'}
      </button>
    </li>
  )
}

/* -------------------------------- 音色库 -------------------------------- */

type Props = {
  /** 音色分组（**接口下发**，分组名/顺序/条数全部照抄，见头注 ①） */
  groups: VoiceGroup[]
  /** 当前生效的音色 ID */
  value: string
  onChange: (voiceId: string) => void
  /** 当前引擎的默认音色（后端下发）—— 用来打「默认」标，也是回落目标 */
  defaultId: string
  /** 引擎展示名（如「MiniMax 官方直连」） */
  engineLabel: string
  /** 本引擎实际调用的合成模型（如 `speech-2.8-hd`） */
  model: string
  /** 清单降级的说明（有值 = 清单没读到、正在用兜底；null = 正常） */
  notice: string | null
  sourcePage?: string
}

export function VoiceLibrary({
  groups,
  value,
  onChange,
  defaultId,
  engineLabel,
  model,
  notice,
  sourcePage,
}: Props) {
  const preview = useVoicePreview(sourcePage)
  const [q, setQ] = useState('')
  /**
   * 输入框的值与列表过滤**分开**：303 行里做一次全量过滤 + 重渲染是有成本的，
   * 直接绑 `q` 会在快速打字时掉帧。`useDeferredValue` 让输入框先响应，
   * 列表稍后追上 —— 用户看到的是「边打边筛」而不是「越打越卡」。
   */
  const dq = useDeferredValue(q)
  /** 分组筛选（null = 全部）。真源是接口下发的分组名 */
  const [group, setGroup] = useState<string | null>(null)
  /** 页签：清单来源（全部 / 收藏） */
  const [tab, setTab] = useState<'all' | 'fav'>('all')
  const [favs, setFavs] = useLocalStorage<string[]>('voiceFavs', [])
  const searchRef = useRef<HTMLInputElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)

  /** 兜一层：localStorage 里被塞进过非数组的话，`.includes` 会当场抛错把整页打白 */
  const favList = Array.isArray(favs) ? favs : []

  const byId = useMemo(() => {
    const m = new Map<string, VoiceInfo>()
    for (const g of groups) for (const v of g.voices) m.set(v.id, v)
    return m
  }, [groups])

  /**
   * 音色 → 头像。**整份清单算一次**，不在每一行里各自算 —— 理由见
   * `buildAvatarMap` 的头注（挑脸依赖清单顺序，行内看不到全局）。
   */
  const avatarById = useMemo(() => buildAvatarMap(groups), [groups])

  const total = useMemo(() => groups.reduce((n, g) => n + g.voices.length, 0), [groups])

  /** 收藏里在当前引擎下**不存在**的那些（换过引擎就会这样）—— 如实说出来，别假装是空的 */
  const staleFavs = useMemo(
    () => favList.filter((id) => !byId.has(id)).length,
    [favList, byId],
  )

  const sections = useMemo(() => {
    const kw = dq.trim().toLowerCase()
    return groups
      .filter((g) => !group || g.label === group)
      .map((g) => ({
        label: g.label,
        voices: g.voices.filter((v) => {
          if (tab === 'fav' && !favList.includes(v.id)) return false
          if (!kw) return true
          // 分组名也参与匹配：用户搜「粤语」时，那 6 条的音色名里一个「粤」字都没有
          return (
            v.id.toLowerCase().includes(kw) ||
            v.name.toLowerCase().includes(kw) ||
            g.label.toLowerCase().includes(kw)
          )
        }),
      }))
      .filter((g) => g.voices.length)
  }, [groups, dq, group, tab, favList])

  const shown = sections.reduce((n, g) => n + g.voices.length, 0)
  const current = byId.get(value)
  const currentAvatar = avatarById.get(value) ?? null
  const dirty = Boolean(q.trim()) || group !== null
  const curIsThis = preview.active === value

  const toggleFav = (id: string) =>
    setFavs((prev) => {
      const list = Array.isArray(prev) ? prev : []
      return list.includes(id) ? list.filter((x) => x !== id) : [...list, id]
    })

  /** 「音色库」那张卡右侧的圆钮：把注意力送到搜索框（这是这一屏唯一能省时间的动作） */
  const focusSearch = () => {
    listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    searchRef.current?.focus()
  }

  return (
    <div className="vlib">
      {notice ? (
        <div className="vlib__notice">
          <Icon name="alert" size={14} />
          <span>{notice}</span>
        </div>
      ) : null}

      {/* ---------- 两张状态卡（参考站的「音色设计 / 音色克隆」位，内容换成我们真有的事实） ---------- */}
      <div className="vlib__hero">
        <div className="vlib__card">
          {/*
            图标位 = **当前音色的头像**（原先是一枚通用的声波图标）。
            这一格回答的是「当前是谁的声音」，一张脸比一个波形更直接；
            而且它与列表里那一条用的是**同一张**脸（都从 `avatarById` 取）——
            两处各自挑的话，用户会看到「卡片上这张、列表里那张」，
            而它们是同一条音色。取不到时回落到原来的波形图标。
          */}
          <span className="vlib__cardIcon">
            {currentAvatar ? (
              <img className="vlib__cardAvatar" src={currentAvatar} alt="" aria-hidden="true" />
            ) : (
              <Icon name="wave" size={20} />
            )}
          </span>
          <span className="vlib__cardText">
            <span className="vlib__cardLabel">当前音色</span>
            <span className="vlib__cardTitle">{current?.name ?? value}</span>
            {/*
              ⚠️ 副标题**只放引擎名**：加上模型名（`speech-2.8-hd`）在这一栏里会被
              截成「MiniMax 官方直连 · speech-2.8-h…」—— 一个被截断的模型名
              比不显示更糟（用户会以为模型就叫这个）。模型名放右边那张卡，
              那里空间够、而且「这一档用什么模型」本来就是那张卡该答的。
            */}
            <span className="vlib__cardSub">
              {engineLabel}
              {preview.heard(value) ? ' · 已听过' : ''}
            </span>
          </span>
          <button
            type="button"
            className="vlib__orb"
            title={curIsThis && preview.playing ? '停止试听' : '试听当前音色'}
            aria-label="试听当前音色"
            onClick={() => preview.toggle(value)}
          >
            {curIsThis && preview.busy ? (
              <span className="vlib__spin" />
            ) : (
              <Icon name={curIsThis && preview.playing ? 'pause' : 'play'} size={15} />
            )}
          </button>
        </div>

        <div className="vlib__card">
          <span className="vlib__cardIcon">
            <Icon name="layers" size={20} />
          </span>
          <span className="vlib__cardText">
            <span className="vlib__cardLabel">音色库</span>
            <span className="vlib__cardTitle">{total} 条</span>
            {/*
              副标题只放模型名，`执行` 也不写：这一栏 ~150px，多一个字就截断。
              skill 名（`minimax-tts`）没丢，它在右栏那张「这条链路」卡里写着 ——
              同一屏里说两遍不如说一遍清楚。
            */}
            <span className="vlib__cardSub">{model}</span>
          </span>
          <button
            type="button"
            className="vlib__orb"
            title="去搜索音色"
            aria-label="去搜索音色"
            onClick={focusSearch}
          >
            <Icon name="search" size={15} />
          </button>
        </div>
      </div>

      <div className="vlib__stat">
        已收藏 {favList.length} 条 · 本引擎共 {total} 条
      </div>

      {/* ---------- 页签 ---------- */}
      <div className="vlib__tabs" role="tablist" aria-label="音色清单来源">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'all'}
          className="vlib__tab"
          data-active={tab === 'all'}
          onClick={() => setTab('all')}
        >
          音色库
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'fav'}
          className="vlib__tab"
          data-active={tab === 'fav'}
          onClick={() => setTab('fav')}
        >
          收藏音色
        </button>
      </div>

      {/* ---------- 搜索 ---------- */}
      <div className="vlib__tools">
        <label className="vlib__search">
          <Icon name="search" size={15} />
          <input
            ref={searchRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="搜索音色名或上游 ID"
            aria-label="搜索音色"
          />
          {q ? (
            <button type="button" className="vlib__clear" title="清空" onClick={() => setQ('')}>
              <Icon name="x" size={13} />
            </button>
          ) : null}
        </label>
        <button
          type="button"
          className="vlib__reset"
          disabled={!dirty}
          title={dirty ? '清空搜索与分组筛选' : '当前没有筛选条件'}
          onClick={() => {
            setQ('')
            setGroup(null)
            setTab('all')
          }}
        >
          <Icon name="refresh" size={14} />
          重置
        </button>
      </div>

      {/* ---------- 按分组（快捷入口；分组名与条数全部来自接口） ---------- */}
      <div className="vlib__try">
        <span className="vlib__tryLabel">按分组：</span>
        <div className="chips">
          <button
            type="button"
            className="chip"
            data-active={group === null}
            onClick={() => setGroup(null)}
          >
            全部 {total}
          </button>
          {groups.map((g) => (
            <button
              key={g.label}
              type="button"
              className="chip"
              data-active={group === g.label}
              onClick={() => setGroup(group === g.label ? null : g.label)}
            >
              {g.label} {g.voices.length}
            </button>
          ))}
        </div>
      </div>

      {tab === 'fav' && staleFavs > 0 ? (
        <div className="vlib__hintLine">
          收藏里有 {staleFavs} 条在当前引擎（{engineLabel}）下不存在，已暂不显示 —— 换过引擎的话，音色 ID 是两套。
        </div>
      ) : null}

      {/* ---------- 列表 ---------- */}
      <div className="vlib__list" ref={listRef}>
        {sections.length ? (
          sections.map((s) => (
            <section key={s.label} className="vlib__section">
              <h4 className="vlib__sectionHead">
                {s.label}
                <span className="vlib__sectionCount">{s.voices.length}</span>
              </h4>
              <ul className="vlib__rows">
                {s.voices.map((v) => (
                  <VoiceRow
                    key={v.id}
                    voice={v}
                    /* 兜底那一下理论上不会命中（map 覆盖全清单）；真命中了给个中性脸，
                       总比空 `src` 让浏览器去请求当前页面地址强（那是「破图标」的经典成因） */
                    avatar={avatarById.get(v.id) ?? '/voice-avatars/neutral-1.webp'}
                    active={v.id === value}
                    isDefault={v.id === defaultId}
                    fav={favList.includes(v.id)}
                    preview={preview}
                    onPick={() => onChange(v.id)}
                    onFav={() => toggleFav(v.id)}
                  />
                ))}
              </ul>
            </section>
          ))
        ) : (
          <div className="vlib__empty">
            {tab === 'fav' && !dq.trim() && group === null
              ? '还没有收藏。点音色右边的 ☆ 收起来 —— 只存在本机浏览器，下次打开还在。'
              : `没有匹配的音色${dq.trim() ? `（搜的是「${dq.trim()}」）` : ''}。换个词，或者点「重置」。`}
          </div>
        )}
      </div>

      <div className="vlib__foot">
        当前显示 {shown} / {total} 条
        {group ? ` · 只看「${group}」` : ''}
        {tab === 'fav' ? ' · 只看收藏' : ''}
      </div>
    </div>
  )
}
